import { extractContactFields } from '../../modules/reports/contact-details';
/**
 * App-data → Reports adapters. These are the ONLY bridge between live
 * application data (MappedLeadData, CampaignMetadata, MinimalLeadData) and
 * the Reports module's template payloads. Reports core never imports app types.
 */
import { validateSqlReport, type SqlReport, type ReportSection, type ReportOpportunity, type ReportEvent } from '../../modules/reports/sql-report';
import { type SectionConfig } from '../../modules/reports/section-catalog';
import { validateCampaignReport, type CampaignReport, type CampaignStage } from '../../modules/reports/campaign-report';
import { buildPipelineFlowStagesFromCounts, getStatusCountsFromAggregate, PIPELINE_FLOW_LABELS } from '../../utils/campaignMetrics';
import { STATUS_MAP, getNormalizedStatusKey, type StatusType } from '../../utils/statusMapping';
import { formatUsd as formatOpportunityUsd, formatTimeline as formatOpportunityTimeline, opportunityDigest } from '../../modules/reports/opportunities';
import type { CampaignOpportunityTypeKey } from '../../modules/reports/campaign-config';
import type { MappedLeadData, MinimalLeadData, CampaignMetadata } from '../../types';

const parse = (v: any, fallback: any = {}) => { try { return typeof v === 'string' ? JSON.parse(v) : v ?? fallback; } catch { return fallback; } };
const record = (v: any): v is Record<string, any> => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v: any): string => typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
const list = (v: any): any[] => Array.isArray(v) ? v : [];
const strings = (v: any): string[] => list(v).map(str).filter(Boolean);
const readable = (v: any) => str(v).replace(/_/g, ' ');
/** "active" → "Active", "on_hold" → "On Hold" — the app's status display form. */
const titleCase = (v: string) => v.trim().replace(/\b\w/g, c => c.toUpperCase());
const appwriteBase = 'https://appwrite-prod-u12020.vm.elestio.app/v1';
const PROJECT_ID = '693c0579001e8b06c6d6';

/** A `lead-images` bucket URL for a file id. Both the lead photo and the company
 *  logo live in that bucket, so they share this one builder. */
const leadImageUrl = (id: unknown) => str(id) && /^[\w-]{1,36}$/.test(str(id)) ? `${appwriteBase}/storage/buckets/lead-images/files/${str(id)}/view?project=${PROJECT_ID}` : undefined;

/**
 * Company logo URL, mirroring `n8nTransformers.companyLogo` — the app card's own
 * rule, so the report and the UI never disagree about which logo a company has:
 *   1. an explicit URL the backend supplies,
 *   2. an explicit storage file id,
 *   3. the COMPANY id as a `lead-images` file id.
 * Returns undefined when nothing resolves — the masthead then shows the company
 * name alone, exactly as it did before.
 *
 * Deliberately NO person-id fallback: the person id is the LEAD PHOTO's file id
 * in the same bucket, so falling back to it renders the lead's own face as the
 * company logo — worse than showing no logo.
 */
const companyLogoUrl = (lvl1Company: any, lvl1Person: any) => {
  const explicit = str(lvl1Company?.logo_url).trim();
  if (/^https:\/\//.test(explicit)) return explicit;
  return leadImageUrl(str(lvl1Company?.logo_storage_id).trim() || str(lvl1Company?.id).trim() || str(lvl1Person?.company_id).trim());
};

/** USD range + timeline display — single source in modules/reports/opportunities.ts
 *  (shared with the campaign report's aggregation). */
const formatUsd = (value: any): string => formatOpportunityUsd(value);
const timelineLabel = (value: any): string => formatOpportunityTimeline(value);

export interface ReportBranding { name: string; color?: string; logoUrl?: string }
export interface LeadReportSource {
  lead: MappedLeadData;
  branding: ReportBranding;
  campaignName: string;
  timezone?: string;
  /** ISO capture time; defaults to now. */
  capturedAt?: string;
}

/** Build the shared identity/brand block for a lead report. */
function leadBase(src: LeadReportSource) {
  const lead = src.lead;
  const raw = (lead.rawOriginalData || {}) as any;
  const lvl1Person = raw.lvl1_person || lead.personLvl1 || {};
  const lvl1Company = raw.lvl1_company || lead.companyLvl1 || {};
  // Prefer the same per-lead channel overrides used by the ISR workspace.
  const lvl2 = raw.lvl2 || lead.personLvl2 || {};
  const personContacts = { ...lvl1Person };
  const companyContacts = { ...lvl1Company };
  for (const [key,value] of Object.entries(lvl2)) {
    if (typeof value !== 'string' || !value.trim()) continue;
    if (key.startsWith('company_') && key.endsWith('_id')) companyContacts[key.slice(8)] = value;
    else if (key.endsWith('_id') && !['company_id','campaign_id','lvl1_id'].includes(key)) personContacts[key] = value;
  }
  const prefs = raw.appwrite_user?.prefs || raw.prefs || {};
  const timezone = str(prefs.timezone) || src.timezone || 'UTC';
  const locale = (str(prefs.language) || 'en').split('-')[0].toLowerCase();
  const name = str(lvl1Person.name) || str(lead.leadName);
  const role = str(lvl1Person.title) || str(lead.leadRole);
  const company = str(lvl1Company.name) || str(lead.companyName);
  const location = str(lvl1Person.full_address) || [str(lvl1Person.city), str(lvl1Person.country)].filter(Boolean).join(', ') || str(lead.companyCountry);
  const campaignId = str(lead.campaignId) || str(lvl1Person.campaign_id);
  const leadId = str(raw.lvl2?.lvl1_id) || str(lvl1Person.id) || str(lead.id);
  return {
    timezone, locale, name, role, company, location, campaignId, leadId,
    reference: [str(campaignId), str(leadId)].filter(Boolean).join('_').slice(0, 120),
    photoUrl: leadImageUrl(lvl1Person.id || (lead as any).id),
    companyLogo: companyLogoUrl(lvl1Company, lvl1Person),
    lvl1Person, lvl1Company, personContacts, companyContacts, raw, lvl2: raw.lvl2 || lead.personLvl2 || {},
  };
}

const brandBlock = (src: LeadReportSource) => ({
  name: src.branding.name || 'Brainiacs',
  color: /^#[\da-f]{6}$/i.test(str(src.branding.color)) ? str(src.branding.color) : '#7C4DFF',
  ...(src.branding.logoUrl ? { logoUrl: src.branding.logoUrl } : {}),
});

function buildSections(base: ReturnType<typeof leadBase>): ReportSection[] {
  const { raw, lvl2 } = base;
  const p = base.lvl1Person;
  const c = base.lvl1Company;
  const campaign = raw.campaign || {};
  const profile = parse(p.profile_intelligence);
  const summary = parse(p.summary);
  const icp = parse(lvl2.icp);
  const strategy = parse(lvl2.personalization_strategy);
  const psych = profile.psychological_profile || {};
  const social = profile.social_intelligence || {};
  const temporal = profile.temporal_intelligence || {};
  const cognitive = profile.cognitive_bias_profile || {};
  const network = profile.network_intelligence || {};
  const sections: (ReportSection & { opportunities?: ReportOpportunity[] })[] = [];
  const add = (id: string, group: ReportSection['group'], title: string, icon: string, body = '', facts: { label: string; value: string }[] = [], bullets: string[] = [], opportunities: ReportOpportunity[] = []) => {
    sections.push({ id, group, title, icon, body, facts: facts.filter(f => f.value), bullets, ...(opportunities.length ? { opportunities } : {}) });
  };
  const fact = (label: string, value: any) => ({ label, value: str(value) });
  add('briefing','overview','Executive intelligence summary','summary', str(lvl2.overview || summary.executive_overview));
  add('fit','overview','Why this lead fits','target', str(icp.justification || icp.reasoning || icp.strategic_justification), [], [icp.top_pain && `Top pain: ${str(icp.top_pain)}`, icp.value_hook && `Value hook: ${str(icp.value_hook)}`, (icp.trigger_event || icp.trigger) && `Trigger event: ${str(icp.trigger_event || icp.trigger)}`].filter(Boolean));
  add('next','overview','Recommended next action','move', str(strategy.call_to_action || strategy.opening_approach || profile.engagement_framework?.decision_facilitation));
  add('person','dossier','Professional profile','person', str(summary.professional_profile?.overview || summary.professional_position?.overview), [fact('Role',p.title || p.role),fact('Location',p.location || [p.city,p.country].filter(Boolean).join(', '))], strings(summary.professional_position?.key_responsibilities));
  add('company','dossier','Company context','company', str(c.overview || c.description), [fact('Industry',c.industry), fact('Website',c.website),fact('Company size',c.employees || c.company_size),fact('Headquarters',c.location || c.city)], strings(summary.professional_profile?.key_expertise));
  add('decision','dossier','Decision mechanics','decision', str(psych.buyer_archetype?.description || psych.decision_making_style), [fact('Buyer archetype',readable(psych.buyer_archetype?.primary_type || psych.buyer_archetype?.type)), fact('Decision role',readable(psych.decision_role))]);
  add('campaign','dossier','Campaign context','signal', str(campaign.summary || campaign.objective || campaign.primary_campaign_objective));
  add('opening','engagement','Opening approach','chat', str(strategy.opening_approach), [], strings(strategy.talking_points));
  add('friction','engagement','Friction & topics to avoid','warning', '', [], [...strings(strategy.avoid_language),...strings(social.icebreaker_intelligence?.avoid_topics),...strings(profile.cultural_intelligence?.cultural_red_flags)]);
  add('objections','engagement','Anticipated objections','shield', '', [], list(cognitive.predicted_objections).map(o => [str(o.objection), str(o.counter_strategy)].filter(Boolean).join('\n')).filter(Boolean));
  const hooks = social.icebreaker_intelligence || {};
  add('starters','engagement','Conversation starters','chat', '', [], [...list(hooks.tier_1_hooks), ...list(hooks.tier_2_hooks), ...list(hooks.tier_3_hooks)].map(h => [str(h.opening_line),str(h.transition)].filter(Boolean).join('\n')).filter(Boolean));
  add('timing','engagement','Engagement timing','clock', '', [fact('Preferred channel', strategy.channel_priority),...list(temporal.optimal_send_times).map(t => fact(str(t.day),[str(t.time),str(t.timezone)].filter(Boolean).join(' ')))]);
  // Structured opportunities — the SAME records the ISR Opportunities tab
  // (components/Tabs/OpportunitiesTab.tsx) renders as Sellers-style cards:
  // lvl2.opportunities is the campaign-scoped list, lvl1_person.opportunities
  // the general/market one. Rendered as cards by the lead-sql renderer; the
  // flat `bullets` fallback below keeps older flows (and the bullets key the
  // renderer already handles) when a record lacks usable prose.
  const oppRecords = (v: any) => list(parse(v, [])).filter(o => record(o) && str(o.need || o.description).trim());
  const oppCard = (o: any) => ({
    need: str(o.need || o.description),
    ...(o.type ? { type: str(o.type) } : {}),
    ...(o.category ? { category: str(o.category) } : {}),
    ...(o.urgency ? { urgency: str(o.urgency) } : {}),
    ...(o.confidence !== undefined && o.confidence !== null && Number.isFinite(Number(o.confidence)) ? { confidence: String(o.confidence) } : {}),
    ...(formatUsd(o.amount_usd) ? { amount: formatUsd(o.amount_usd) } : {}),
    ...(o.timeline_months ? { timeline: timelineLabel(o.timeline_months) } : {}),
    ...(o.country ? { country: str(o.country) } : {}),
    ...(o.decision_maker ? { decisionMaker: str(o.decision_maker) } : {}),
    ...(list(o.evidence).length ? { evidence: list(o.evidence).map(str).filter(Boolean).slice(0, 20) } : {}),
  });
  add('opportunities','opportunities','Campaign opportunities','opportunity','',[],[], oppRecords(lvl2.opportunities).map(oppCard));
  add('market','opportunities','Market opportunities','globe','',[],[], oppRecords(p.opportunities).map(oppCard));
  add('committee','opportunities','Buying committee & stakeholders','network','',[],list(network.internal_network?.department_colleagues).map(m => [str(m.name),str(m.title),readable(m.decision_influence)].filter(Boolean).join(' — ')));
  // Placeholder section for the contact grid — the renderer swaps in the
  // contact cards when this id is present (and the grid is skipped when not).
  sections.push({ id:'contacts', group:'dossier', title:'Company & person contacts', icon:'chat' });
  return sections;
}

function buildTimeline(base: ReturnType<typeof leadBase>, warnings: string[]): ReportEvent[] {
  const { lvl2, raw } = base;
  const notesDoc = parse(lvl2.notes || raw.notes, { notes: [] });
  const timeline: ReportEvent[] = [];
  const addEvent = (e: ReportEvent) => {
    if (!Number.isFinite(Date.parse(e.at))) { warnings.push(`Activity without a valid date was omitted: ${e.title || e.id}`); return; }
    timeline.push({ ...e, at: new Date(e.at).toISOString() });
  };
  list(notesDoc.notes).filter((n: any) => n.activity_type !== 'interaction_summary').forEach((n: any, i: number) =>
    addEvent({ id:`note-${n.id || i}`, at:str(n.created_at), kind:n.activity_type === 'status_change' ? 'status' : 'note', author:str(n.created_by_name || n.created_by) || 'Unspecified author', title:str(n.title) || (n.activity_type === 'status_change' ? 'Status changed' : 'SDR note'), body:str(n.body) }));
  list(raw.interactions || lvl2.interactions).forEach((e: any, i: number) =>
    addEvent({ id:`interaction-${e.id || i}`, at:str(e.timestamp || e.created_at), kind:['email','call','message'].includes(e.type) ? e.type : 'message', author:str(e.participantName || e.author) || 'Unspecified author', title:str(e.meta?.subject || e.title || readable(e.type)) || 'Interaction', body:str(e.content || e.body) }));
  return timeline;
}

function buildSummary(base: ReturnType<typeof leadBase>, warnings: string[]): SqlReport['interactionSummary'] {
  const { lvl2 } = base;
  const notesDoc = parse(lvl2.notes, { notes: [] });
  const saved = list(notesDoc.notes).filter((n: any) => n.activity_type === 'interaction_summary').sort((a: any, b: any) => Date.parse(b.created_at) - Date.parse(a.created_at))[0];
  if (!saved) return null;
  if (!Number.isFinite(Date.parse(saved.created_at))) { warnings.push('The saved interaction summary has no valid date and was omitted.'); return null; }
  return { body:str(saved.body), author:str(saved.created_by_name || saved.created_by) || 'Unspecified author', at:new Date(saved.created_at).toISOString() };
}

/** MappedLeadData → SQL/MQL report payload (schema-validated). */
export function leadToReport(src: LeadReportSource, variant: 'mql' | 'sql', sectionConfig?: SectionConfig): { report: SqlReport; warnings: string[]; leadId: string; campaignId: string } {
  const base = leadBase(src);
  const warnings: string[] = [];
  const lvl2 = base.lvl2 as any;
  const score = lvl2.icp_score;
  const allSections = buildSections(base);
  if (sectionConfig?.signals) {
    const fit = allSections.find(s => s.id === 'fit');
    if (fit?.bullets) fit.bullets = fit.bullets.filter(b => sectionConfig.signals!.includes(b.startsWith('Top pain:') ? 'pain' : b.startsWith('Trigger event:') ? 'trigger' : 'hook'));
  }
  // Admin config: filter to visible sections and reorder to match the
  // saved order. If no config → all sections in default order (backward-compatible).
  const sections = sectionConfig
    ? (sectionConfig.sections
        .map(id => allSections.find(s => s.id === id))
        .filter((s): s is ReportSection => Boolean(s)))
    : allSections;
  const report: SqlReport = {
    schemaVersion: 1,
    locale: base.locale, timezone: base.timezone,
    capturedAt: src.capturedAt || new Date().toISOString(),
    reference: base.reference,
    brand: brandBlock(src),
    lead: { name: base.name || 'Unnamed lead', role: base.role, company: base.company, location: base.location, ...(base.photoUrl ? { photoUrl: base.photoUrl } : {}), ...(base.companyLogo ? { companyLogoUrl: base.companyLogo } : {}) },
    contacts: {
      company: [
        ...[['Industry',base.lvl1Company.industry],['Employees',base.lvl1Company.nr_employees || base.lvl1Company.employees || base.lvl1Company.size_range],['Revenue',base.lvl1Company.revenue_range],['Founded',base.lvl1Company.founded_year || base.lvl1Company.founded],['Headquarters',base.lvl1Company.full_address || base.lvl1Company.location || [base.lvl1Company.city,base.lvl1Company.country].filter(Boolean).join(', ')]].filter(([,v]) => v != null && v !== '').map(([label,value]) => ({label: str(label),value: str(value)})),
        ...extractContactFields(base.companyContacts),
      ],
      person: [
        ...[['Role',base.role],['Location',base.location],['Seniority',base.lvl1Person.seniority],['Department',base.lvl1Person.department],['Language',base.lvl1Person.language]].filter(([,v]) => v != null && v !== '').map(([label,value]) => ({label: str(label),value: str(value)})),
        ...extractContactFields(base.personContacts),
      ],
    },
    campaign: src.campaignName,
    status: readable(lvl2.status || (src.lead as any).status),
    icpScore: score !== null && score !== undefined && score !== '' && Number.isFinite(Number(score)) ? Number(score) : null,
    ...(sectionConfig ? { groupOrder: sectionConfig.groupOrder as ReportSection['group'][] } : {}),
    sections,
    timeline: variant === 'sql' ? buildTimeline(base, warnings) : [],
    interactionSummary: variant === 'sql' ? buildSummary(base, warnings) : null,
  };
  validateSqlReport(report);
  return { report, warnings, leadId: base.leadId, campaignId: base.campaignId };
}

/** Campaign + its leads → Campaign Overview payload (schema-validated). */
export function campaignToReport(
  campaign: CampaignMetadata,
  leads: MinimalLeadData[],
  branding: ReportBranding,
  options: { withLeadList: boolean; timezone?: string; capturedAt?: string; /** Directory variant payload marker (companies/leads templates). */ directory?: 'companies' | 'leads'; /** Restrict directory rows to these normalized statuses. */ directoryStatuses?: StatusType[]; /** Opportunity sheets to include (campaign-scoped and/or general market). */ opportunityTypes?: CampaignOpportunityTypeKey[] } ,
): { report: CampaignReport; warnings: string[]; campaignId: string } {
  const warnings: string[] = [];
  const prefs = (campaign as any).rawOriginalData?.appwrite_user?.prefs || {};
  // Normalize the raw aggregate through the SHARED reader first — raw n8n keys
  // ("message_ready", "writting_message") get bucketed and every missing
  // status becomes a real 0. Building stages from the raw object yields
  // undefined values that fail template validation (the dark/empty report bug).
  const stageList: CampaignStage[] = buildPipelineFlowStagesFromCounts(getStatusCountsFromAggregate(parse(campaign.leads_by_status, null)))
    // `discarded` is excluded from the forward funnel, so it gets no highlight.
    .map(s => ({ key: s.key, label: s.label, value: s.value, color: STATUS_MAP[s.key].color, highlight: ['enriched','outreaching','outreached','engaged'].includes(s.key) }));
  // The discarded COUNT and the discarded LEAD ROWS come from different sources
  // and can disagree: `leads_by_status` carries no discarded bucket in the live
  // data shape (status_count vs fresh_lead_count), while the `leads` array does
  // contain discarded rows. Dropping those rows on a zero aggregate count
  // silently removes leads from the directory. So the requirement is the
  // strongest of the two — a rendered row always has its stage, which the
  // schema enforces (a row whose status is not a stage needs a statusLabel).
  const discardedLeadCount = leads.filter(isDiscardedRow).length;
  const stages: CampaignStage[] = stageList.some(s => s.key === 'discarded') || discardedLeadCount === 0
    ? stageList
    : [...stageList, {
        key: 'discarded',
        label: PIPELINE_FLOW_LABELS.discarded,
        value: discardedLeadCount,
        color: STATUS_MAP.discarded.color,
      }];
  // Directory variants (campaign-companies / campaign-leads templates): filter
  // to the requested pipeline slice and, for the companies view, deduplicate by
  // company (first lead anchors the row) and populate the company-profile
  // fields the card already carries. campaign-overview passes no option and
  // keeps today's all-status lead directory.
  const statusFilter = options.directoryStatuses;
  const seenCompanies = new Set<string>();
  const leadRows = leads
    .filter(l => !statusFilter || statusFilter.includes(getNormalizedStatusKey(l.status)))
    .filter(l => {
      if (options.directory !== 'companies') return true;
      const key = str(l.companyName).trim().toLowerCase();
      if (!key || seenCompanies.has(key)) return false;
      seenCompanies.add(key);
      return true;
    })
    .map(l => {
      const statusKey = getNormalizedStatusKey(l.status);
      const location = [str(l.companyCity), str(l.companyCountry)].filter(Boolean).join(', ');
      const base = {
        name: str(l.leadName) || 'Unnamed lead',
        role: str(l.leadRole),
        company: str(l.companyName),
        statusKey,
      };
      return {
        ...base,
        companyLogoUrl: str(l.companyLogo) || undefined,
        location: location || undefined,
        employeeRange: str(l.size_range) || str(l.nr_employees) || undefined,
        // One-line digest of this lead's strongest opportunity (enabled types
        // only) — undefined when the lead has none or the toggle is off.
        opportunity: opportunityDigest(l, options.opportunityTypes ?? []),
      };
    });
  const report: CampaignReport = {
    schemaVersion: 1,
    locale: (str(prefs.language) || 'en').split('-')[0].toLowerCase(),
    timezone: str(prefs.timezone) || options.timezone || 'UTC',
    capturedAt: options.capturedAt || new Date().toISOString(),
    // Variant tag in the reference (—co / —ld): the deployed proxy derives the
    // storage file id from THIS value, so each directory variant publishes at
    // its own id instead of overwriting the overview PDF. Dashes pass the
    // proxy's identifier check.
    reference: (str(campaign.id) + (options.directory === 'companies' ? '-co' : options.directory === 'leads' ? '-ld' : '')).slice(0, 120),
    brand: { name: branding.name || 'Brainiacs', color: /^#[\da-f]{6}$/i.test(str(branding.color)) ? str(branding.color) : '#7C4DFF', ...(branding.logoUrl ? { logoUrl: branding.logoUrl } : {}) },
    // Status renders Title Case to match the app's own display convention
    // (CampaignDetailsModal.formatStatusLabel) — the raw value is lowercase
    // ("active"), which is a closed-vocabulary key, not a display string.
    campaign: { name: str(campaign.name) || 'Campaign', status: titleCase(readable(campaign.status || 'active')), objective: str(campaign.primary_campaign_objective) || undefined },
    stages,
    ...(options.directory ? { directory: options.directory } : {}),
    leads: options.withLeadList ? leadRows : [],
  };
  if (!options.withLeadList && leads.length === 0) warnings.push('This campaign has no leads yet; the directory will be empty.');
  validateCampaignReport(report);
  return { report, warnings, campaignId: str(campaign.id) };
}
const isDiscardedRow = (l: MinimalLeadData) => getNormalizedStatusKey(l.status) === 'discarded';
